import { useCallback, useEffect, useState } from 'react'
import { type RouteComponentProps } from 'react-router-dom'
import { toast } from 'react-toastify'

import { Layout } from '@loadsmart/loadsmart-ui'

import { AsyncContent } from 'core/ui/async-content/AsyncContent'
import { LoadingState } from 'core/ui/async-content/LoadingState'
import { useAbortController } from 'core/ui/hooks/useAbortController'
import { MainContent } from 'core/ui/MainContent'

import { useRefreshInviteToken } from 'auth/ui/hooks/useRefreshInviteToken'

import { canSubmitRates, canUpdateRates, hasRunningProcess } from '../domain/Proposal'
import { RfpTabOptions } from '../domain/RfpTabs'
import { Attachments } from './Attachments'
import { ErrorState } from './ErrorState'
import { ExpiredInviteState } from './ExpiredInviteState'
import { useProposal } from './hooks/useProposal'
import { ProposalPageBanners } from './ProposalPageBanners'
import { RfpDetails } from './RfpDetails'
import { RfpTabs } from './RfpTabs'

type ProposalPageProps = {
  match: {
    params: {
      id: string
    }
  }
  disabled: boolean
} & Pick<RouteComponentProps, 'location' | 'history'>

export function ProposalPage({ disabled = false, match }: ProposalPageProps) {
  const { id } = match.params
  const {
    proposal,
    proposalStatus,
    isSubmittingProposal,
    isAutoSubmittingProposal,
    isAutoSubmitEnabled,
    isWithdrawingProposal,
    isTokenExpired,
    fetchProposal,
    onSubmitProposal,
    onWithdrawProposal,
    onToggleAutoSubmitProposal,
    pollProposal,
  } = useProposal()
  const { emailRecipient, refreshInviteToken, refreshInviteTokenStatus } = useRefreshInviteToken()
  const abortController = useAbortController([id])

  const [hasReviewedAccessorialsTab, setHasReviewedAccessorialsTab] = useState<boolean>(false)
  const [hasFilledAccessorialRate, setHasFilledAccessorialRate] = useState<boolean>(false)

  const shouldDisableSubmit = disabled || isSubmittingProposal || !canSubmitRates(proposal)
  const shouldDisableRate = disabled || isSubmittingProposal || !canUpdateRates(proposal)
  const [pollingRunning, setPollingRunning] = useState(false)

  const refetchProposal = useCallback(async () => {
    await fetchProposal(id, abortController)
  }, [abortController, id, fetchProposal])

  const handleOnSubmitProposal = useCallback(async () => {
    try {
      await onSubmitProposal(id)
    } catch (error) {
      toast.error('Error while submitting the proposal')
      return
    }

    refetchProposal()
  }, [id, onSubmitProposal, refetchProposal])

  const handleOnWithdrawProposal = useCallback(async () => {
    try {
      await onWithdrawProposal(id)
    } catch (error) {
      toast.error('Error while withdrawing the proposal')
      return
    }

    refetchProposal()
  }, [id, onWithdrawProposal, refetchProposal])

  const handleToggleAutoSubmitProposal = useCallback(
    async (autoSubmit: boolean) => {
      try {
        await onToggleAutoSubmitProposal(id, autoSubmit, abortController)
      } catch (error) {
        toast.error('Error while changing proposal auto submit')
      }
    },
    [id, abortController, onToggleAutoSubmitProposal]
  )

  const handleOnChangeProposal = useCallback(async () => {
    try {
      await onSubmitProposal(id)
    } catch (error) {
      toast.error('Error while changing the proposal')
      return
    }

    refetchProposal()
  }, [id, onSubmitProposal, refetchProposal])

  const handleRfpTabChange = useCallback((tab: string) => {
    if (tab === RfpTabOptions.Accessorials) {
      setHasReviewedAccessorialsTab(true)
    }
  }, [])

  const handleSetHasFilledAccessorialRate = useCallback((result: boolean) => {
    setHasFilledAccessorialRate(result)
  }, [])

  useEffect(() => {
    fetchProposal(id, abortController)

    return () => {
      abortController.abort()
    }
  }, [id, abortController, fetchProposal])

  useEffect(() => {
    if (isTokenExpired) {
      refreshInviteToken({ carrierInviteLinkUuid: id }, { signal: abortController.signal })
    }
  }, [abortController, id, isTokenExpired, refreshInviteToken])

  useEffect(() => {
    if (pollingRunning) {
      if (hasRunningProcess(proposal)) {
        pollProposal(id, abortController)
      } else {
        refetchProposal()
        setPollingRunning(false)
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abortController, id, proposal, pollProposal])

  return (
    <MainContent>
      <AsyncContent
        status={proposalStatus}
        loadingState={<LoadingState />}
        errorState={
          isTokenExpired ? (
            <ExpiredInviteState
              emailRecipient={emailRecipient}
              refreshInviteTokenStatus={refreshInviteTokenStatus}
            />
          ) : (
            <ErrorState />
          )
        }
      >
        {proposal && (
          <Layout.Stack space="l">
            <ProposalPageBanners proposal={proposal} proposalId={id} />
            <RfpDetails
              rfpId={id}
              autoSubmit={isAutoSubmitEnabled}
              disabled={disabled}
              proposal={proposal}
              isBidDisabled={shouldDisableSubmit}
              isAutoSubmittingProposal={isAutoSubmittingProposal}
              isWithdrawingProposal={isWithdrawingProposal}
              isSubmittingProposal={isSubmittingProposal}
              onFileUploaded={() => {
                refetchProposal()
                setPollingRunning(true)
              }}
              onSubmitProposal={handleOnSubmitProposal}
              onChangeProposal={handleOnChangeProposal}
              onToggleAutoSubmitProposal={handleToggleAutoSubmitProposal}
              onWithdrawProposal={handleOnWithdrawProposal}
              hasReviewedAccessorialsTab={hasReviewedAccessorialsTab}
              hasFilledAccessorialRate={hasFilledAccessorialRate}
            />
            {!disabled && (
              <Attachments
                attachments={proposal.rfp.attachments}
                rfpId={id}
                rfpName={proposal.rfp.name}
              />
            )}
            <RfpTabs
              rfpId={id}
              proposal={proposal}
              isBidDisabled={shouldDisableRate}
              displayResult={disabled}
              onTabChange={handleRfpTabChange}
              onHasFilledAccessorialRatesChange={handleSetHasFilledAccessorialRate}
            />
          </Layout.Stack>
        )}
      </AsyncContent>
    </MainContent>
  )
}
